import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import {
  ActiveDirectoryUser,
  Rol,
  roleAssignationSchema,
} from "../schema/role-assignation.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserRolesCard } from "@/components/common/section-components/card/UserRolesCard";
import { UserSelectionCard } from "@/components/common/section-components/card/UserSelectionCard";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import { AppRoutes } from "@/constants/routes";
import {
  handleCreateRoleAssignation,
  sucursalSelectionHandler,
} from "../schema/handler-functions";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import { getActiveDirectoryUsers } from "@/services/users-bo.service";

export const UserRoleAssignCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(roleAssignationSchema),
  });

  const [roleSelection, user, sucursalesSelection] = useWatch({
    control,
    name: ["roles", "user", "sucursales"],
  });

  const navigate = useNavigate();
  const [rolesToAssign, setRolesToAssign] = useState<Rol[]>([]);
  const { fetchAllRoles } = useRoles();

  const [loadingCreateUserBO, setLoadingCreateUserBO] =
    useState<boolean>(false);

  const [loadingQueries, setLoadingQueries] = useState<boolean>(false);

  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  const [ADUsers, setADUsers] = useState<ActiveDirectoryUser[]>([]);

  const fetchADUsers = async () => {
    const response = await getActiveDirectoryUsers({});
    setADUsers(response ?? []);
  };

  const fetchRolesToAssign = async () => {
    const response = await fetchAllRoles();

    setRolesToAssign(response?.listado);
  };

  const fetchSucursales = async () => {
    const response = await getSucursales({
      all: true
    });
    setSucursales(response);
  };

  useEffect(() => {
    if (isActive) {
      (async () => {
        setLoadingQueries(true);
        try {
          await Promise.all([
            fetchRolesToAssign(),
            fetchADUsers(),
            fetchSucursales(),
          ]);
        } finally {
          setLoadingQueries(false);
        }
      })();
    }
  }, [isActive]);

  const setRoleSelection = (value?: number[]) => {
    setValue("roles", value ?? []);
  };

  const [isGeneralSelected, setIsGeneralSelected] = useState(false);

  const handleSucursalSelection = (selectedIds: string[]) => {
    sucursalSelectionHandler({
      isGeneralSelected,
      selectedIds,
      setIsGeneralSelected,
      setValue,
      sucursales,
    });
  };

  const { fetchPermissions } = useRoles();

  const onSubmit = async (event: FieldValues) => {
    await handleCreateRoleAssignation({
      data: event,
      fetchPermissions,
      navigate,
      setLoadingCreateUserBO,
      redirectPath: AppRoutes.USER_ROLES,
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="user-role-assign-creation-page"
    >
      <SectionHeader
        title="Asignación de roles y sucursales"
        cancelButtonLabel="Cancelar"
        addButtonLabel="Agregar"
        disableDelete={true}
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        disableAdd={
          !roleSelection?.length || !user?.value || !sucursalesSelection?.length
        }
        loading={loadingQueries || loadingCreateUserBO}
      />

      <UserSelectionCard
        errors={errors}
        control={control}
        handleSucursalSelection={handleSucursalSelection}
        setLoadingADUsers={setLoadingQueries}
        loadingAD={loadingQueries}
        loadingSucursales={loadingQueries}
        loadingOperation={loadingCreateUserBO}
        sucursales={sucursales}
        setValue={setValue}
        ADUsers={ADUsers}
      />

      <UserRolesCard
        roleSelection={roleSelection || []}
        setRoleSelection={setRoleSelection}
        userRoles={rolesToAssign}
      />
    </Flex>
  );
};
